// src/contexts/SessionStorageContext.js
// Enhanced session storage with TTL-based cache validation and consolidated data fetching
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocsFromServer } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { useAuth } from './AuthContext';

const SessionStorageContext = createContext();

// TTL configuration (milliseconds)
const CACHE_TTL = {
  userData: 30 * 60 * 1000,        // 30 minutes (signature/profile rarely changes)
  clients: 5 * 60 * 1000,          // 5 minutes
  notificationCounts: 60 * 1000,   // 1 minute (more volatile)
};

const STORAGE_KEY = 'emailAdminAppCache';

export const useSessionStorage = () => {
  const context = useContext(SessionStorageContext);
  if (!context) {
    throw new Error('useSessionStorage must be used within a SessionStorageProvider');
  }
  return context;
};

export const SessionStorageProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [cache, setCache] = useState({
    userData: null,           // { data: {...}, timestamp: number }
    clients: null,            // { data: [...], timestamp: number }
    notificationCounts: null, // { data: {...}, timestamp: number }
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Track if we've already fetched data for this user
  const fetchedForUserRef = useRef(null);
  const isFetchingRef = useRef(false);

  // Ref for fetchUserData to break dependency cycle in useEffects
  const fetchUserDataRef = useRef(null);

  // Keep a ref to the current cache to avoid dependency cycles
  const cacheRef = useRef(cache);
  cacheRef.current = cache;

  // Check if a cache entry is valid based on TTL (uses ref to avoid recreating on cache changes)
  const isCacheValid = useCallback((key) => {
    const entry = cacheRef.current[key];
    if (!entry || !entry.timestamp) return false;
    const ttl = CACHE_TTL[key] || CACHE_TTL.userData;
    return (Date.now() - entry.timestamp) < ttl;
  }, []);

  // Get cache data - returns stale data if available (stale-while-revalidate pattern)
  // This ensures UI always has data to show while refreshing in background
  const getCacheData = useCallback((key) => {
    return cache[key]?.data ?? null;
  }, [cache]);

  // Check if cache needs refresh (expired but may still have data)
  const needsRefresh = useCallback((key) => {
    const entry = cacheRef.current[key];
    if (!entry || !entry.timestamp) return false;
    const ttl = CACHE_TTL[key] || CACHE_TTL.userData;
    const isValid = (Date.now() - entry.timestamp) < ttl;
    return !isValid && entry.data;
  }, []);

  // Set cache data with timestamp
  const setCacheData = useCallback((key, data) => {
    setCache(prev => ({
      ...prev,
      [key]: data === null ? null : { data, timestamp: Date.now() }
    }));
  }, []);

  // Load cache from sessionStorage on mount
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Validate structure and restore cache
        if (parsed && typeof parsed === 'object') {
          setCache(prev => ({
            ...prev,
            userData: parsed.userData || null,
            // Older getUserData summaries omit completion. Do not paint them
            // as Live before the authoritative marker has been loaded.
            clients: Array.isArray(parsed.clients?.data)
              && parsed.clients.data.every(client => (
                Object.prototype.hasOwnProperty.call(client, 'completion')
                // Rows cached before the stop kind was hydrated cannot tell a
                // terminal stop from a user-requested one, so Delete would stay
                // disabled on them; refetch instead of painting them.
                && Object.prototype.hasOwnProperty.call(client, 'stopKind')
              ))
              ? parsed.clients : null,
            notificationCounts: parsed.notificationCounts || null,
          }));
        }
      }
    } catch (err) {
      console.error('Error loading session storage cache:', err);
    }
  }, []);

  // Persist cache to sessionStorage when it changes
  useEffect(() => {
    try {
      const dataToStore = {
        userData: cache.userData,
        clients: cache.clients,
        notificationCounts: cache.notificationCounts,
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(dataToStore));
    } catch (err) {
      console.error('Error saving to session storage:', err);
    }
  }, [cache]);

  // Minimum loading duration for smooth UX (shows skeleton animations)
  const MIN_LOADING_MS = 1000;

  // Fetch all user data from the backend Cloud Function
  const fetchUserData = useCallback(async (force = false) => {
    if (!currentUser) {
      setIsLoading(false);
      setIsInitialized(true);
      return null;
    }

    // Prevent duplicate fetches
    if (isFetchingRef.current && !force) {
      return null;
    }

    // Check if we have valid cached data
    if (!force && isCacheValid('userData') && isCacheValid('clients')) {
      setIsLoading(false);
      setIsInitialized(true);
      return { cached: true };
    }

    isFetchingRef.current = true;
    setIsLoading(true);
    setError(null);

    const loadingStartTime = Date.now();

    try {
      const getUserDataFn = httpsCallable(functions, 'getUserData');
      // getUserData projects client fields and omits completion. Hydrate
      // from client documents before publishing rows, including on reload.
      // A server read avoids replacing a signed marker with a stale cache;
      // it does not depend on the callable, so both run at once.
      const [result, clientSnapshot] = await Promise.all([
        getUserDataFn(),
        getDocsFromServer(collection(db, 'users', currentUser.uid, 'clients')),
      ]);

      if (result.data) {
        const { userProfile, organizationName, clients, notificationCounts, fetchedAt } = result.data;

        const clientDocuments = new Map(clientSnapshot.docs.map(snapshot => [snapshot.id, snapshot.data()]));
        const currentClients = clients.filter(client => (
          clientDocuments.has(client.id) && !clientDocuments.get(client.id).pendingDelete
        ));

        // Format clients with proper date strings for display
        const formattedClients = currentClients.map(client => {
          const updatedAt = new Date(client.lastUpdated);
          const formatted = [
            updatedAt.toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: '2-digit'
            }),
            updatedAt.toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit'
            })
          ].join(' ');

          const clientDocument = clientDocuments.get(client.id);
          return {
            ...client,
            completion: clientDocument.completion ?? null,
            // getUserData's projection also omits the stop kind and the delete
            // clean-up markers; the Delete gate (ClientsPage.canDeleteCampaign)
            // and the clean-up resume read them from the row.
            stopKind: clientDocument.stopKind ?? null,
            cleanupStatus: clientDocument.cleanupStatus ?? null,
            cleanupFailure: clientDocument.cleanupFailure ?? null,
            lastUpdated: formatted,
          };
        });

        // Update all cache entries
        setCacheData('userData', { userProfile, organizationName });
        setCacheData('clients', formattedClients);
        setCacheData('notificationCounts', notificationCounts);

        console.log(`✅ SessionStorage: fetched ${formattedClients.length} clients at ${new Date(fetchedAt).toLocaleTimeString()}`);
      }

      // Ensure minimum loading time for smooth skeleton animation
      const elapsed = Date.now() - loadingStartTime;
      const remainingDelay = Math.max(0, MIN_LOADING_MS - elapsed);

      if (remainingDelay > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingDelay));
      }

      setIsLoading(false);
      setIsInitialized(true);
      fetchedForUserRef.current = currentUser.uid;
      return result.data;

    } catch (err) {
      console.error('Error fetching user data:', err);
      setError(err.message || 'Failed to fetch user data');

      // Still respect minimum loading time on error
      const elapsed = Date.now() - loadingStartTime;
      const remainingDelay = Math.max(0, MIN_LOADING_MS - elapsed);
      if (remainingDelay > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingDelay));
      }

      setIsLoading(false);
      setIsInitialized(true);
      return null;
    } finally {
      isFetchingRef.current = false;
    }
  }, [currentUser, isCacheValid, setCacheData]);

  // Keep ref in sync with latest fetchUserData
  fetchUserDataRef.current = fetchUserData;

  // Fetch data when user changes or on initial mount
  // Note: We use fetchUserDataRef.current instead of fetchUserData in deps
  // to prevent infinite re-renders from the callback reference changing
  useEffect(() => {
    if (!currentUser) {
      // Clear cache when user logs out
      setCache({
        userData: null,
        clients: null,
        notificationCounts: null,
      });
      setIsLoading(false);
      setIsInitialized(true);
      fetchedForUserRef.current = null;
      return;
    }

    // Check if we already fetched for this user
    if (fetchedForUserRef.current === currentUser.uid && isInitialized) {
      return;
    }

    fetchUserDataRef.current?.();
  }, [currentUser, isInitialized]);

  // Convenience accessors
  const getUserProfile = useCallback(() => {
    const userData = getCacheData('userData');
    return userData?.userProfile ?? null;
  }, [getCacheData]);

  const getOrganizationName = useCallback(() => {
    const userData = getCacheData('userData');
    return userData?.organizationName ?? '';
  }, [getCacheData]);

  const getClients = useCallback(() => {
    return getCacheData('clients') ?? [];
  }, [getCacheData]);

  const getNotificationCounts = useCallback(() => {
    return getCacheData('notificationCounts') ?? {};
  }, [getCacheData]);

  // Refresh all data from backend
  const refreshAllData = useCallback(() => {
    return fetchUserData(true);
  }, [fetchUserData]);

  // Invalidate specific cache entry
  const invalidateCache = useCallback((key) => {
    setCacheData(key, null);
  }, [setCacheData]);

  // Clear all cache (e.g., on logout)
  const clearCache = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    setCache({
      userData: null,
      clients: null,
      notificationCounts: null,
    });
    setIsInitialized(false);
    fetchedForUserRef.current = null;
  }, []);

  // Update a specific client in the cache (for mutations)
  const updateClientInCache = useCallback((clientId, updates) => {
    setCache(prev => {
      if (!prev.clients?.data) return prev;
      const updatedClients = prev.clients.data.map(client =>
        client.id === clientId ? { ...client, ...updates } : client
      );
      return {
        ...prev,
        clients: { data: updatedClients, timestamp: prev.clients.timestamp }
      };
    });
  }, []);

  // Add a new client to the cache
  const addClientToCache = useCallback((client) => {
    setCache(prev => {
      const currentClients = prev.clients?.data ?? [];
      return {
        ...prev,
        clients: { data: [...currentClients, client], timestamp: Date.now() }
      };
    });
  }, []);

  // Remove a client from the cache
  const removeClientFromCache = useCallback((clientId) => {
    setCache(prev => {
      if (!prev.clients?.data) return prev;
      const filteredClients = prev.clients.data.filter(c => c.id !== clientId);
      return {
        ...prev,
        clients: { data: filteredClients, timestamp: prev.clients.timestamp }
      };
    });
  }, []);

  // Update user profile in cache
  const updateUserProfileInCache = useCallback((updates) => {
    setCache(prev => {
      if (!prev.userData?.data) return prev;
      return {
        ...prev,
        userData: {
          data: {
            ...prev.userData.data,
            userProfile: { ...prev.userData.data.userProfile, ...updates }
          },
          timestamp: Date.now()  // Refresh TTL on update
        }
      };
    });
  }, []);

  // Update organization name in cache
  const updateOrganizationNameInCache = useCallback((orgName) => {
    setCache(prev => {
      if (!prev.userData?.data) return prev;
      return {
        ...prev,
        userData: {
          data: {
            ...prev.userData.data,
            organizationName: orgName
          },
          timestamp: Date.now()  // Refresh TTL on update
        }
      };
    });
  }, []);

  // Update notification count for a specific client
  const updateNotificationCountInCache = useCallback((clientId, count) => {
    setCache(prev => {
      const currentCounts = prev.notificationCounts?.data ?? {};
      return {
        ...prev,
        notificationCounts: {
          data: { ...currentCounts, [clientId]: count },
          timestamp: Date.now()
        }
      };
    });
  }, []);

  // Background refresh when cache is stale (stale-while-revalidate)
  // Note: We use refs for fetchUserData and needsRefresh to prevent infinite re-renders
  useEffect(() => {
    if (currentUser && isInitialized && !isFetchingRef.current) {
      if (needsRefresh('userData') || needsRefresh('clients')) {
        console.log('📦 Cache stale, refreshing in background...');
        fetchUserDataRef.current?.(true);
      }
    }
  }, [currentUser, isInitialized, needsRefresh]);

  // Backward compatibility: sessionData object for gradual migration
  const sessionData = useMemo(() => {
    const cachedClients = getClients();
    const hasClients = cachedClients.length > 0;

    return {
      userProfile: getUserProfile(),
      organizationName: getOrganizationName(),
      // Show cached clients immediately if available, otherwise wait for initialization
      clients: hasClients ? cachedClients : (isInitialized ? [] : null),
      notificationCounts: getNotificationCounts(),
      isLoadingProfile: isLoading && !getUserProfile(),
      // Loading if not initialized OR actively loading with no clients
      isLoadingClients: !isInitialized || (isLoading && !hasClients),
      emailSignature: getUserProfile()?.emailSignature ?? null,
      signatureMode: getUserProfile()?.signatureMode ?? null,
    };
  }, [getUserProfile, getOrganizationName, getClients, getNotificationCounts, isLoading, isInitialized]);

  // Backward compatibility: updateSessionData
  const updateSessionData = useCallback((key, value) => {
    if (key === 'clients') {
      setCacheData('clients', value);
    } else if (key === 'userProfile') {
      updateUserProfileInCache(value);
    } else if (key === 'organizationName') {
      updateOrganizationNameInCache(value);
    } else if (key === 'emailSignature' || key === 'signatureMode') {
      updateUserProfileInCache({ [key]: value });
    }
  }, [setCacheData, updateUserProfileInCache, updateOrganizationNameInCache]);

  // Backward compatibility: setLoadingState (no-op since loading is managed internally)
  const setLoadingState = useCallback(() => {
    // Loading is now managed internally
  }, []);

  // Backward compatibility: clearSessionData
  const clearSessionData = useCallback(() => {
    clearCache();
  }, [clearCache]);

  const value = useMemo(() => ({
    // New API
    getUserProfile,
    getOrganizationName,
    getClients,
    getNotificationCounts,
    refreshAllData,
    invalidateCache,
    clearCache,
    updateClientInCache,
    addClientToCache,
    removeClientFromCache,
    updateUserProfileInCache,
    updateOrganizationNameInCache,
    updateNotificationCountInCache,
    isLoading,
    isInitialized,
    error,
    isCacheValid,

    // Backward compatibility
    sessionData,
    updateSessionData,
    setLoadingState,
    clearSessionData,
  }), [
    getUserProfile,
    getOrganizationName,
    getClients,
    getNotificationCounts,
    refreshAllData,
    invalidateCache,
    clearCache,
    updateClientInCache,
    addClientToCache,
    removeClientFromCache,
    updateUserProfileInCache,
    updateOrganizationNameInCache,
    updateNotificationCountInCache,
    isLoading,
    isInitialized,
    error,
    isCacheValid,
    sessionData,
    updateSessionData,
    setLoadingState,
    clearSessionData,
  ]);

  return (
    <SessionStorageContext.Provider value={value}>
      {children}
    </SessionStorageContext.Provider>
  );
};
